import {  Component, OnInit } from '@angular/core';
import { AuthService } from 'src/shared/service/auth.service';
import { AbstractDataProvider } from '../AbstractDataProvider';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { GenericFilterModel } from '../security/QueryStringParam';
import { SortDescriptor } from '@progress/kendo-data-query';
import { Report } from '../../shared/model/interfaces';
import {  SelectionEvent } from '@progress/kendo-angular-grid';
import { DictionaryPipe } from '../../shared/pipes/dictionary.pipe';
import { finalize } from 'rxjs/operators';
import {getErrorMessageText} from '../helper';

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
  providers: [DictionaryPipe]
})
export class ReportsComponent implements OnInit {

  public reports: Report[] = [];
  public display: boolean;
  public alertMessage: string;
  public rowSelectionList = [];
  public rowPerPage = 50;
  public currentPageNo = 1;
  public introText: string;
  public loading: boolean;
  public sort: SortDescriptor[] = [{field: 'Created', dir: 'desc'}];
  public displayConfirmationPopup: boolean;

  private totalRecords: number;
  private skipRecords = 0;
  private allDataLoaded = false;

  get isAllSelected() {
    return this.totalRecords === this.rowSelectionList.length;
  }

  constructor(private authService: AuthService,
              private abstractDataProvider: AbstractDataProvider,
              private ngxService: NgxUiLoaderService,
              private dictionaryPipe: DictionaryPipe) {
  }

  ngOnInit() {
    this.getIntroText();
    this.getData(this.rowPerPage);
  }

  onConfirm() {
    this.display = false;
  }

  sortChange(sort: SortDescriptor[]): void {
    if (sort && sort[0].field === 'SeqNum') { // TODO: check
      return;
    }
    this.currentPageNo = 1;
    this.allDataLoaded = false;
    this.sort = sort;
    this.reports = [];
    this.skipRecords = 0;
    this.getData(this.rowPerPage);
  }

  loadMore(countToLoad: number = null, afterLoad: () => void = null) {
    if (this.loading)
      return;

    if (!this.allDataLoaded) {
      this.skipRecords = this.reports.length;
      this.getData(countToLoad || this.rowPerPage, afterLoad);
    } else {
      afterLoad();
    }
  }

  downloadDocument(event: Event, DocSeqNum: any, FileNum: any) {
    event.stopPropagation();
    event.preventDefault();
    this.abstractDataProvider.getData('api/File/IsFileExist?SeqNum=' + DocSeqNum + '&forWhat=position')
      .subscribe(
        posts => {
          if (posts.Message) {
            this.display = true;
            this.alertMessage = posts.Message;
          } else if (posts.DocSeqNum) {
            // window.open(this.abstractDataProvider.baseUrl + 'api/File/FileDownloadFromDocumentImage?SeqNum=' + posts.DocSeqNum + '&forWhat=position&CurrentUserNumber=' + posts.CurrentUserNumber + '&FileNum=0', '_blank');
            const url = this.abstractDataProvider.baseUrl + 'api/File/FileDownloadFromDocumentImage?SeqNum=' + posts.DocSeqNum + '&forWhat=position&CurrentUserNumber=' + posts.CurrentUserNumber + '&FileNum=0';
                this.abstractDataProvider.downloadFile(url)
                .subscribe(_ => {}, error =>{
                  this.display = true;
                  this.alertMessage = getErrorMessageText(error);
                });
          } else {
            // Not Found
            this.display = true;
            this.alertMessage = this.dictionaryPipe.transform('FILE_NOT_FOUND');
          }
        },
        error => {
          this.display = true;
          this.alertMessage =  getErrorMessageText(error);
        }
      );
  }

  downloadSelected() {
    if (!this.rowSelectionList || this.rowSelectionList.length <= 0) {
      this.display = true;
      this.alertMessage = this.dictionaryPipe.transform('SELECT_ANY_REPORTS_FOR_DOWNLOAD');
      return;
    }

    if (this.isAllSelected) {
      this.loadMore(this.totalRecords - this.reports.length, () => {
        const fileExistInfo = this.reports.map(dataItem => {
          return {FileNum: dataItem.FileNum, SeqNum: dataItem.SeqNum, ForWhat: 'position', Label: dataItem.Label};
        });
        this.bulkDownload(fileExistInfo);
      });
    } else {
      const fileExistInfo = this.rowSelectionList.map((seqNum) => {
        const dataItem = this.reports.find(r => r.SeqNum === seqNum);
        return {FileNum: dataItem.FileNum, SeqNum: dataItem.SeqNum, ForWhat: 'position', Label: dataItem.Label};
      });
      this.bulkDownload(fileExistInfo);
    }
  }

  private bulkDownload(fileExistInfo) {
    this.abstractDataProvider.postData(fileExistInfo, 'api/File/IsFilesExistBulk')
      .subscribe(
        posts => {
          if (posts.Message) {
            this.display = true;
            this.alertMessage = posts.Message;
          } else if (posts.Data) {
            const foundFileList = [];
            let messageToShow = '';
            posts.Data.forEach((d) => {
              if (!d.IsExist) {
                if (messageToShow) {
                  messageToShow += ' ; ';
                }
                messageToShow += d.Label;
              } else {
                foundFileList.push(d);
              }
            });
            if (messageToShow) {
              messageToShow = this.dictionaryPipe.transform('FILES_COULD_NOT_BE_FOUND') + messageToShow;
              this.display = true;
              this.alertMessage = messageToShow;
            }
            if (foundFileList && foundFileList.length > 0) {
              foundFileList.forEach((post) => {
                // window.open('api/File/FileDownloadFromDocumentImage?SeqNum=' + post.SeqNum + '&forWhat=position&CurrentUserNumber=' + post.CurrentUserNumber + '&FileNum=' + post.FileNum, '_blank');
                const url = this.abstractDataProvider.baseUrl + 'api/File/FileDownloadFromDocumentImage?SeqNum=' + post.SeqNum + '&forWhat=position&CurrentUserNumber=' + post.CurrentUserNumber + '&FileNum=' + post.FileNum;
                this.abstractDataProvider.downloadFile(url)
                  .subscribe(_ => {}, error =>{
                    this.display = true;
                    this.alertMessage = getErrorMessageText(error);
                  });
              });
            }
          }
          this.rowSelectionList = [];
        },
        error => {
          this.display = true;
          this.alertMessage =  getErrorMessageText(error);
        }
      );
  }

  askConfirmation(): void {
    this.alertMessage = this.dictionaryPipe.transform('NEW_POSITION_FILE_CONFIRMATION_MESSAGE');
    this.displayConfirmationPopup = true;
  }

  addNewReport(event: boolean): void {
    if (!event) {
      return;
    }

    this.abstractDataProvider.postData({}, 'api/Report/SubmitReportForUser')
      .subscribe(() => {
        this.resetData();
        this.getData(this.rowPerPage);
        this.display = true;
        this.alertMessage = this.dictionaryPipe.transform('REPORT_SENT');
      }, error => {
        this.display = true;
        this.alertMessage =  getErrorMessageText(error);
      });
  }

  onSelection(event: SelectionEvent) {
    this.rowSelectionList = this.rowSelectionList.filter(seqNum => {
      const report = this.reports.find(r => r.SeqNum === seqNum);
      return report.DocSeqNum !== 0;
    });
  }

  private resetData() {
    this.reports = [];
    this.totalRecords = null;
    this.skipRecords = 0;
    this.allDataLoaded = false;
    this.rowSelectionList = [];
  }

  private getIntroText() {
    this.introText = this.authService.getIntroText('REPORTS');
  }

  private getData(countToLoad: number, afterLoad: () => void = null): void {
    this.loading = true;
    const genericFilterModel = new GenericFilterModel();
    genericFilterModel.PageNo = this.currentPageNo;
    genericFilterModel.PageSize = countToLoad;
    genericFilterModel.SortField = this.sort[0].field;
    genericFilterModel.SortOrder = this.sort[0].dir;
    genericFilterModel.SkipRecords = this.skipRecords;
    this.rowSelectionList = [];
    this.abstractDataProvider.postData(genericFilterModel, 'api/Report/GetReportsForUser')
      .pipe(finalize(() => {
        this.loading = false;
    }))
      .subscribe((data: {Data: Report[], TotalCount: number}) => {
          if (genericFilterModel.SkipRecords === 0) {
            this.totalRecords = data.TotalCount;
          }
          if (data.Data.length > 0) {
            this.reports = [...this.reports, ...data.Data];
            if (this.totalRecords === this.reports.length) {
              this.allDataLoaded = true;
            }
          } else {
            this.allDataLoaded = true;
          }
          if (afterLoad) {
            afterLoad();
          }
        },
        error => {
          this.display = true;
          this.alertMessage =  getErrorMessageText(error);
        });
  }
}
